import React from "react";
import { w2pt, izoom } from "../../utils/coords";

/**
 * NodeLayer — renders wall junction nodes.
 *
 * CAD behaviour: nodes are invisible unless:
 *   - selected (user clicked it)
 *   - being dragged
 *   - an error node (broken topology)
 *   - a dangling endpoint (deg=1) — shown faintly to indicate open end
 *   - mode=select (show all endpoints to help user pick)
 *
 * The hit target is always present (transparent circle) for interaction.
 */
export default function NodeLayer({
  st, selNodeId, onSelect, onDragStart,
  dragNodeId, dragPosMm,
  dragNodes, dragDelta,
  zoom, mode, selManyNodes,
}) {
  const s = izoom(zoom);
  const wallsLayer = (st.layers || {}).walls;
  if (wallsLayer?.visible === false) return null;

  const brkN = new Set(
    (st._errors || []).filter(e => ["B","D"].includes(e.rule)).flatMap(e => e.ids || [])
  );

  const showAll = mode === "select"; // in select mode, show all so user can click

  // Build map of live positions for multi-drag nodes
  const multiDragMap = dragNodes && dragDelta
    ? Object.fromEntries(dragNodes.map(({ id, x0, y0 }) => [
        id, { x: x0 + (dragDelta.dx || 0), y: y0 + (dragDelta.dy || 0) }
      ]))
    : null;

  return (
    <g>
      {Object.values(st.nodes).map(n => {
        const deg    = n.halfEdgeIds.length / 2;
        const isSel  = n.id === selNodeId;
        const isDrag = n.id === dragNodeId;
        const isBad  = brkN.has(n.id);
        const isDangling = deg === 1; // open endpoint
        const isOrphan   = deg === 0; // completely disconnected
        const isMultiSel = (selManyNodes || []).includes(n.id);

        const multiDragPos = multiDragMap?.[n.id];
        const isMultiDrag = !!multiDragPos;
        const pos = isDrag && dragPosMm ? w2pt(dragPosMm)
                  : isMultiDrag       ? w2pt(multiDragPos)
                  : w2pt(n);

        // Visibility decision
        const visible = isSel || isDrag || isBad || isMultiSel || isMultiDrag || (showAll && deg > 0);

        // Faint hint for dangling endpoints even when not in select mode
        const showDanglingHint = isDangling && !isSel && !isDrag && !showAll;

        const hitR = s * 10; // invisible hit target — always present

        return (
          <g key={n.id}>
            {/* Invisible hit target — always active */}
            <circle
              cx={pos.x} cy={pos.y} r={hitR}
              fill="transparent" stroke="none"
              style={{ cursor: "move" }}
              onClick={ev => { ev.stopPropagation(); onSelect(n.id); }}
              onMouseDown={ev => { ev.stopPropagation(); ev.preventDefault(); onDragStart(n.id); }}
            />

            {/* Dangling endpoint hint — very faint tick mark */}
            {showDanglingHint && !isOrphan && (
              <circle cx={pos.x} cy={pos.y} r={s * 2.5}
                fill="none" stroke="#3b82f640"
                strokeWidth={s * 0.6} opacity={0.5}/>
            )}

            {/* Error node */}
            {isBad && (
              <circle cx={pos.x} cy={pos.y} r={s * 8}
                fill="none" stroke="#f87171"
                strokeWidth={s * 0.8}
                strokeDasharray={`${s * 3},${s * 2}`}
                opacity={0.7}/>
            )}

            {/* Selection ring */}
            {isSel && (
              <circle cx={pos.x} cy={pos.y} r={s * 9}
                fill="#3b82f610" stroke="#3b82f6"
                strokeWidth={s * 0.5}
                strokeDasharray={`${s * 3},${s * 2}`}/>
            )}

            {/* Multi-select ring */}
            {isMultiSel && !isSel && (
              <circle cx={pos.x} cy={pos.y} r={s * 7}
                fill="#3b82f620" stroke="#3b82f6"
                strokeWidth={s * 0.5} style={{ pointerEvents:"none" }}/>
            )}

            {/* Visible node dot — only when needed */}
            {(visible || isBad) && (
              <circle
                cx={pos.x} cy={pos.y}
                r={s * (isDrag ? 4 : isSel ? 3.5 : isBad ? 3.5 : 2.5)}
                fill={
                  isBad   ? "#f87171" :
                  isDrag  ? "#e8a838" :
                  isSel   ? "#3b82f6" :
                  deg === 1 ? "#3b82f650" :
                  "#3b82f630"
                }
                stroke={
                  isBad   ? "#f8717180" :
                  isDrag  ? "#e8a83880" :
                  isSel   ? "#3b82f680" :
                  "#3b82f630"
                }
                strokeWidth={s * 0.6}
                style={{ pointerEvents: "none" }}
              />
            )}

            {/* Multi-drag indicator */}
            {isMultiDrag && (
              <circle cx={pos.x} cy={pos.y} r={s * 5}
                fill="none" stroke="#e8a838"
                strokeWidth={s * 0.6}
                strokeDasharray={`${s * 2},${s * 2}`}
                opacity={0.7}/>
            )}

            {/* Single-node drag position indicator */}
            {isDrag && (
              <circle cx={pos.x} cy={pos.y} r={s * 6}
                fill="none" stroke="#e8a838"
                strokeWidth={s * 0.5}
                strokeDasharray={`${s * 2},${s * 2}`}
                opacity={0.6}/>
            )}
          </g>
        );
      })}
    </g>
  );
}
